export const SCANNER_HANDOFF_VERSION = 1 as const;

export type ScannerHandoff = {
  version: typeof SCANNER_HANDOFF_VERSION;
  source: "scanner";
  vendorText: string;
  dateYmd: string;
  amount: string;
  sourceDocumentId: number;
};

export type ScannerRouteState = {
  scannerHandoff: ScannerHandoff;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isValidYmd(value: string): boolean {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const [, year, month, day] = match;
  const parsed = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day)),
  );

  return (
    parsed.getUTCFullYear() === Number(year) &&
    parsed.getUTCMonth() === Number(month) - 1 &&
    parsed.getUTCDate() === Number(day)
  );
}

export function createScannerHandoff(input: {
  vendorText: string;
  dateYmd: string;
  amount: string;
  sourceDocumentId?: number | null;
}): ScannerHandoff | null {
  const vendorText = input.vendorText.trim();
  const amountNumber = Number(input.amount.replace(/,/g, "").trim());
  const sourceDocumentId = Number(input.sourceDocumentId);

  if (!vendorText) return null;
  if (!isValidYmd(input.dateYmd)) return null;
  if (!Number.isFinite(amountNumber) || amountNumber <= 0) return null;
  if (!Number.isInteger(sourceDocumentId) || sourceDocumentId <= 0) return null;

  return {
    version: SCANNER_HANDOFF_VERSION,
    source: "scanner",
    vendorText,
    dateYmd: input.dateYmd,
    amount: amountNumber.toFixed(2),
    sourceDocumentId,
  };
}

export function scannerHandoffFromRouteState(
  state: unknown,
): ScannerHandoff | null {
  if (!isRecord(state)) return null;

  const candidate = isRecord(state.scannerHandoff)
    ? state.scannerHandoff
    : state;

  if (
    candidate.version !== SCANNER_HANDOFF_VERSION ||
    candidate.source !== "scanner" ||
    typeof candidate.vendorText !== "string" ||
    typeof candidate.dateYmd !== "string" ||
    typeof candidate.amount !== "string" ||
    typeof candidate.sourceDocumentId !== "number"
  ) {
    return null;
  }

  return createScannerHandoff({
    vendorText: candidate.vendorText,
    dateYmd: candidate.dateYmd,
    amount: candidate.amount,
    sourceDocumentId: candidate.sourceDocumentId,
  });
}
